import { useParams, Link } from 'react-router-dom';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <SEOHead title="文章不存在" />
        <Navbar />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 text-center">
          <div className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章不存在</h1>
          <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被删除。</p>
          <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '吃瓜网站',
    },
  };

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.chigua.site/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.chigua.site/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',') + ',吃瓜网站,51吃瓜网站,吃瓜网站线路一'}
        ogType="article"
        ogImage={article.coverImage}
        schema={[articleSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main>
        <article>
          <div className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              title={article.title}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-transparent"></div>
          </div>

          <div className="max-w-[800px] mx-auto px-4 md:px-6 -mt-24 relative z-10">
            <div className="bg-background-50 rounded-lg p-6 md:p-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />

              <header className="mt-5">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-xs bg-primary-100 text-primary-700 px-3 py-1 rounded-full font-medium whitespace-nowrap">{article.category}</span>
                  {article.isHot && (
                    <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium whitespace-nowrap">热门</span>
                  )}
                  {article.isNew && (
                    <span className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full font-medium whitespace-nowrap">最新</span>
                  )}
                </div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 leading-snug">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {article.viewCount.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center"></i>
                    {article.commentCount.toLocaleString()} 评论
                  </span>
                </div>
              </header>

              <div className="mt-8" dangerouslySetInnerHTML={{ __html: article.content }}>
              </div>

              <div className="mt-8 pt-6 border-t border-secondary-200/70">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1.5 rounded-full whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1.5 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}